import {Alert, AlertTitle, CircularProgress, IconButton, Stack, TextField, Typography} from "@mui/material";
import {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {AudienceService} from "crqlar-react-common/dist/services/audience/audience.service";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    useAllAudienceProfileFilterOptions,
    useAudienceProfileFilterOptions,
    useEstimatedAudienceSize,
    useEstimatedMarketingAudienceSize
} from "crqlar-react-common/dist/utils/hooks/audience/useAudience";
import {getFilterOptionLabel, getFilterOptionValue} from "crqlar-react-common/dist/utils/filter.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {isEqual} from "lodash";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {MultiSelect} from "../common/newSelect";
import {type AudienceMemberMode, createAudienceMemberListUrl} from "./audienceMemberUrlState";
import NewAudienceFilters from "./NewAudienceFilters";

export interface AudienceFormProps {
    /** The working copy being edited — the caller owns it and hands back every change. */
    audience: Audience
    onChange: (audience: Audience) => void
    /**
     * The saved baseline: unchanged rules can open members by id; edited rules preview the draft
     * filter. Counts always come from the live filter query, since saved sizes can be stale.
     * A brand-new audience passes none.
     */
    savedAudience?: Audience
    readOnly?: boolean
}

/**
 * The body of the audience editor: name, channels, the two live size cards, and the rules block.
 *
 * It exists as its own component because there are two places that edit an audience — the marketing
 * module's `AudienceDialog` and the automation editor's Audience step, which embeds the same form
 * so the size preview is right there while a flow is wired up. Everything around the form (the
 * dialog chrome, who saves it, what happens afterwards) belongs to the caller; the form itself is
 * fully controlled and never talks to the server except to count.
 */
const AudienceForm = ({audience, onChange, savedAudience, readOnly}: AudienceFormProps) => {

    const {t} = useTranslation()
    const {language} = useLocalize()

    const [promptFilterApplied, setPromptFilterApplied] = useState<boolean>()

    // No rules means everyone, which is exactly what the backend counts for an empty filter: count from
    // the first render so the cards stay numeric instead of flipping from a dash to a total once a
    // rule has been added and removed.
    const sizedAudience = useMemo(
        () => ({...audience, filter: audience.filter ?? {}}),
        [audience]
    )
    const audienceSizeQ = useEstimatedAudienceSize(sizedAudience)
    const marketingAudienceSizeQ = useEstimatedMarketingAudienceSize(sizedAudience)
    const audienceFilterOptions = useAudienceProfileFilterOptions()
    const audienceFilters = useMemo(() => audienceFilterOptions || [], [audienceFilterOptions])
    // Full option set (before the picker denylist) — used to resolve labels/operators for rules on
    // fields the picker hides (e.g. templates using top-percentile / total-stays / last-stay-date).
    const allAudienceFilterOptions = useAllAudienceProfileFilterOptions()
    const allAudienceFilters = useMemo(() => allAudienceFilterOptions || [], [allAudienceFilterOptions])

    // Ready-made audience templates the user can pick to prefill the filter (still fully overridable).
    const templatesQ = useQuery('audienceTemplates', () => AudienceService.getTemplates(), {
        staleTime: Infinity,
    })
    const templates = useMemo(() => templatesQ.data || [], [templatesQ.data])

    const audienceSize = audienceSizeQ.data ?? 0
    const marketingAudienceSize = marketingAudienceSizeQ.data ?? 0

    // The figure under a size title: a spinner while the backend counts (a large production org can take
    // a moment), then the number. The row keeps the h6 line height so the card never jumps.
    const renderSize = (size: number, loading: boolean) => (
        <Typography variant="h6" component="div" sx={{display: 'flex', alignItems: 'center', minHeight: 32}}>
            {loading
                ? <CircularProgress size={20} color={'inherit'} aria-label={t('Calculating...')}/>
                : t('{{n}} profiles', {n: size.toLocaleString()})}
        </Typography>
    )

    const openAudienceMembers = (mode: AudienceMemberMode) => {
        const useSavedFilter = !!savedAudience?.id && isEqual(audience.filter, savedAudience.filter);
        const url = createAudienceMemberListUrl({
            audienceId: useSavedFilter ? savedAudience.id : undefined,
            filter: useSavedFilter ? undefined : audience.filter ?? {},
            mode
        });
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // When ai generated filter applies - this triggers and sets the suggested audience name
    useEffect(() => {
        if (!audience.filter || !audience.filter.filterGroups?.length) return

        if (!promptFilterApplied) return
        // Only a brand-new audience takes its name from the generated filter.
        if (savedAudience?.id) return

        let filterRule = audience.filter?.filterGroups?.[0]?.filterRules?.[0]

        let fieldName = getFilterOptionLabel(
            allAudienceFilters,
            filterRule?.field,
            language
        )

        let value = getFilterOptionValue(filterRule)

        if (fieldName && value) {
            onChange({
                ...audience,
                name: `${fieldName} ${value}`
            })
        }

        setPromptFilterApplied(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAudienceFilters, language, promptFilterApplied, audience.filter])

    return (
        <Stack gap={2}>
            <Stack direction={{xs: 'column', md: 'row'}} gap={3} alignItems={'flex-start'}>
                <Stack sx={{flex: 3, minWidth: 0}} gap={2}>
                    <TextField
                        id={'audience_name'}
                        inputProps={{autoComplete: 'off'}}
                        label={t('Audience name')}
                        placeholder={t('Give your audience a name you can remember')}
                        variant="outlined"
                        disabled={readOnly}
                        value={audience.name || ''}
                        onChange={e => {
                            onChange({
                                ...audience,
                                name: e.target.value
                            })
                        }}
                        size={'small'}
                        fullWidth
                    />

                    <Stack gap={0.5}>
                        <MultiSelect
                            label={t('Channels')}
                            disabled={readOnly}
                            options={[
                                {value: 'facebook', label: 'Facebook'},
                                {value: 'newsletter', label: t('Newsletter')}
                            ]}
                            value={audience.syncData?.map(sync => sync.channel) || []}
                            onChange={channels => {
                                onChange({
                                    ...audience,
                                    syncData: channels.map(channel => ({channel}))
                                })
                            }}
                        />
                        <Alert severity={'info'}
                               sx={{
                                   backgroundColor: CommonThemeColors.white
                               }}
                        >
                            <Typography
                                variant={'subtitle1'}
                                sx={{
                                    mt: 0.5,
                                    color: CommonThemeColors.sys.text.secondary
                                }}
                            >
                                {t('Select on which channels you want to use the audience to improve your marketing.')}
                            </Typography>
                        </Alert>
                    </Stack>
                </Stack>

                <Stack direction={'row'} sx={{flex: 2, minWidth: 0, width: {xs: '100%', md: 'auto'}}}
                       gap={2}>

                    <Stack direction={"row"} gap={1} sx={{width: '100%'}}>
                        <Alert
                            icon={false}
                            severity="success"
                            sx={{flex: 1, minWidth: 0}}
                            action={
                                <IconButton
                                    color={'inherit'}
                                    size={'small'}
                                    aria-label={t('View members')}
                                    onClick={() => openAudienceMembers('audience')}
                                >
                                    <OpenInNewIcon fontSize={'small'}/>
                                </IconButton>
                            }
                        >
                            <AlertTitle>{t('Audience size')}</AlertTitle>
                            {renderSize(audienceSize, audienceSizeQ.isLoading)}
                        </Alert>
                        <Alert
                            icon={false}
                            severity="info"
                            sx={{flex: 1, minWidth: 0}}
                            action={
                                <IconButton
                                    color={'inherit'}
                                    size={'small'}
                                    aria-label={t('View members')}
                                    onClick={() => openAudienceMembers('marketing')}
                                >
                                    <OpenInNewIcon fontSize={'small'}/>
                                </IconButton>
                            }
                        >
                            <AlertTitle>{t('Marketing audience size')}</AlertTitle>
                            {renderSize(marketingAudienceSize, marketingAudienceSizeQ.isLoading)}
                        </Alert>
                    </Stack>
                </Stack>
            </Stack>

            <NewAudienceFilters
                filter={audience.filter || {}}
                setFilter={filter => {
                    onChange({
                        ...audience,
                        filter: filter,
                        tags: {
                            ...audience.tags,
                            sourceAudience: undefined,
                            audienceTemplate: undefined
                        }
                    })
                }}
                filterOptions={audienceFilters}
                labelOptions={allAudienceFilters}
                readOnly={readOnly}
                templates={templates}
                templatesLoading={templatesQ.isLoading}
                onSelectTemplate={template => {
                    onChange({
                        ...audience,
                        // Keep any name the user already typed; otherwise seed from the template.
                        name: audience.name || t(template.name || ''),
                        filter: template.filter,
                        tags: {
                            ...audience.tags,
                            sourceAudience: undefined,
                            audienceTemplate: template.tags?.audienceTemplate,
                        },
                    })
                }}
                initialQuery={audience.query ?? ''}
                onGenerated={(filter, query) => {
                    onChange({
                        ...audience,
                        filter: filter,
                        query: query,
                        tags: {
                            ...audience.tags,
                            audienceTemplate: undefined
                        }
                    })
                    setPromptFilterApplied(true)
                }}
            />
        </Stack>
    )
}

export default AudienceForm;
